import React from 'react';
import { Helmet } from 'react-helmet-async';

interface StaticMetaTagsProps {
  title: string;
  description: string;
  url: string;
  image?: string;
  type?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
}

export default function StaticMetaTags({
  title,
  description,
  url,
  image = 'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png',
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  section,
  tags = []
}: StaticMetaTagsProps) {
  const fullTitle = title.includes('recruteurs.io') ? title : `${title} | recruteurs.io`;
  const canonicalUrl = url.startsWith('http') ? url : `https://recruteurs.io${url}`;

  // Nettoyer la description pour éviter les caractères problématiques
  const cleanDescription = description
    .replace(/[<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 160);

  return (
    <Helmet>
      {/* Basic Meta Tags - CRITIQUES pour le prerendering */}
      <title>{fullTitle}</title>
      <meta name="description" content={cleanDescription} />
      <meta name="keywords" content={`${section || 'RH'}, recrutement, ${title}`} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph - PRIORITAIRES pour LinkedIn/Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={cleanDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="recruteurs.io" />
      <meta property="og:locale" content="fr_FR" />
      
      {/* Article specific - Pour Google News et rich snippets */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time\" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {author && <meta property="article:author\" content={author} />}
          {section && <meta property="article:section" content={section} />}
          {tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card - Pour Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={cleanDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@recruteurs_io" />
      <meta name="twitter:creator" content="@recruteurs_io" />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content={author || "Recruteurs.io"} />
      
      {/* JSON-LD Structured Data - Pour les rich snippets Google */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": type === 'article' ? 'Article' : 'WebPage',
          "headline": title,
          "description": cleanDescription,
          "url": canonicalUrl,
          "image": {
            "@type": "ImageObject",
            "url": image,
            "width": 1200,
            "height": 630
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": canonicalUrl
          },
          ...(type === 'article' && {
            "datePublished": publishedTime,
            "dateModified": modifiedTime || publishedTime,
            "author": {
              "@type": "Person",
              "name": author || "Recruteurs.io",
              "url": "https://recruteurs.io"
            },
            "publisher": {
              "@type": "Organization",
              "name": "recruteurs.io",
              "url": "https://recruteurs.io",
              "logo": {
                "@type": "ImageObject",
                "url": "https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png",
                "width": 400,
                "height": 100
              }
            },
            "articleSection": section,
            "keywords": tags.join(', ')
          })
        })}
      </script>
    </Helmet>
  );
}